import { useMemo } from 'react';
import type { StationeryTemplate } from '@apps/card/types/stationeryTemplate.types';
import type { GalleryTemplate } from './components/GalleryItem/GalleryItem';
import type { ColorHits } from './useColorHits';
import type { TemplateFacetIndex } from './templateFacetMatching.types';
import { EMPTY_COLORWAY_ORDER } from './useColorHits';
import type { ColorwayOrderSource } from './GalleryColorwayOrderContext.types';
import type { PriceValuePropAsset } from './propValues.types';
import { useTagFilteredTemplates } from './useTagFilteredTemplates';
import { useFuse } from './components/Search/useFuse';
import { useSearchFilteredGalleryItems } from './components/Search/useSearchFilteredGalleryItems';
import { useThemeDisplayOrder, usePriorityThemeId } from './useDesignTemplates';
import { expandTemplatesByDisplayOrder } from './utils';
import { useGalleryItemsWithValueProps } from './useGalleryItemsWithValueProps';
import { useDesignGalleryFiltersContext } from './DesignGalleryFiltersProvider';

const NO_PARTIAL_MATCHES: ReadonlySet<string> = new Set();

type UseDesignGalleryItemsArgs = Readonly<{
  templates: ReadonlyArray<StationeryTemplate>;
  colorHits: ColorHits;
  facetIndex: TemplateFacetIndex;
  /** A filter rail is open beside the grid, which then runs one column narrower. */
  isRailOpen?: boolean;
}>;

/**
 * Derives visible tiles and their matching colorway/search context from the catalog.
 * Must run inside DesignGalleryFiltersProvider and the gallery's search context.
 * Flow: facet filtering → search ranking → display-order expansion → promos → price tile.
 */
export const useDesignGalleryItems = ({ templates, colorHits, facetIndex, isRailOpen = false }: UseDesignGalleryItemsArgs) => {
  const {
    templates: tagFilteredTemplates,
    colorwayOrder: filterColorwayOrder,
    isColorFilterActive,
    isStyleFilterActive,
    partialMatchIds,
  } = useTagFilteredTemplates(templates, colorHits, facetIndex);
  const fuse = useFuse(templates);
  const tagFilteredItems: ReadonlyArray<GalleryTemplate> = useMemo(
    () =>
      tagFilteredTemplates.map(template => ({
        ...template,
        type: 'template',
      })),
    [tagFilteredTemplates],
  );
  const { isActive: isSearchActive, filteredItems, colorwayOrder: searchColorwayOrder, scoresByItem: searchScoresByItem } = useSearchFilteredGalleryItems(tagFilteredItems, fuse);

  // Search already scores every colorway, so when a term is active its ranking
  // supersedes the filter's (coarser) color ordering rather than competing with it.
  const colorwayOrder = useMemo((): Readonly<{ order: ReadonlyMap<string, ReadonlyArray<string>>; source?: ColorwayOrderSource }> => {
    if (isSearchActive) return { order: searchColorwayOrder, source: 'search' };
    if (isColorFilterActive) return { order: filterColorwayOrder, source: 'colorFilter' };
    return { order: EMPTY_COLORWAY_ORDER };
  }, [isSearchActive, searchColorwayOrder, filterColorwayOrder, isColorFilterActive]);

  // Without search, color, or style filtering, expand the list per the gallery-order
  // feature flag: a theme listed multiple times renders as multiple tiles, each
  // pre-selecting the flag's `+colorway`. Both a search term and a color filter
  // ignore the flag — they each pick the colorway a tile should show, which would
  // collapse the flag's deliberately-different repeats into identical tiles.
  const themeOrder = useThemeDisplayOrder();
  const priorityThemeId = usePriorityThemeId();
  // Partial-match fill is a ranking (exact matches first, see useTagFilteredTemplates); the
  // flag's theme order would interleave the two tiers, so it stands down too.
  const isColorwayNarrowed = isSearchActive || isColorFilterActive || isStyleFilterActive || partialMatchIds.size > 0;
  const galleryItems = useMemo(
    () => (!isColorwayNarrowed && themeOrder?.length ? expandTemplatesByDisplayOrder(filteredItems as ReadonlyArray<GalleryTemplate>, themeOrder, priorityThemeId) : filteredItems),
    [isColorwayNarrowed, themeOrder, filteredItems, priorityThemeId],
  );

  const { itemsWithProps, galleryColumnCount } = useGalleryItemsWithValueProps(galleryItems, isSearchActive, isRailOpen);

  const firstTemplate = galleryItems.find((item): item is GalleryTemplate => item.type === 'template');

  const { getTotalActiveFilterCount } = useDesignGalleryFiltersContext();

  const items = (() => {
    if (!firstTemplate || itemsWithProps.length === 0 || isSearchActive || getTotalActiveFilterCount() > 0) return itemsWithProps;
    const priceTile: PriceValuePropAsset & { type: 'priceValueProp' } = {
      type: 'priceValueProp',
      representativeTemplate: firstTemplate,
    };
    const insertAt = Math.min(galleryColumnCount, itemsWithProps.length);
    return [...itemsWithProps.slice(0, insertAt), priceTile, ...itemsWithProps.slice(insertAt)];
  })();

  return {
    items,
    hasNoSearchResults: isSearchActive && !filteredItems.length,
    colorwayOrder,
    searchScoresByItem,
    // Search re-ranks the whole list, so the exact/partial tiers no longer hold under a term.
    partialMatchIds: isSearchActive ? NO_PARTIAL_MATCHES : partialMatchIds,
  };
};
